import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { transactionLineItems } from '../../util/api';
import * as log from '../../util/log';

const requestAction = actionType => params => ({ type: actionType, payload: { params } });

const successAction = actionType => result => ({ type: actionType, payload: result.data });

const errorAction = actionType => error => ({ type: actionType, payload: error, error: true });

const shuffleArray = items => {
  const shuffled = [...items];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
  }

  return shuffled;
};

// ================ Action types ================ //

export const SHOW_LISTING_REQUEST = 'app/ProductPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_SUCCESS = 'app/ProductPage/SHOW_LISTING_SUCCESS';
export const SHOW_LISTING_ERROR = 'app/ProductPage/SHOW_LISTING_ERROR';

export const FETCH_LINE_ITEMS_REQUEST = 'app/ProductPage/FETCH_LINE_ITEMS_REQUEST';
export const FETCH_LINE_ITEMS_SUCCESS = 'app/ProductPage/FETCH_LINE_ITEMS_SUCCESS';
export const FETCH_LINE_ITEMS_ERROR = 'app/ProductPage/FETCH_LINE_ITEMS_ERROR';
export const FETCH_RELATED_LISTINGS_REQUEST = 'app/ProductPage/FETCH_RELATED_LISTINGS_REQUEST';
export const FETCH_RELATED_LISTINGS_SUCCESS = 'app/ProductPage/FETCH_RELATED_LISTINGS_SUCCESS';
export const FETCH_RELATED_LISTINGS_ERROR = 'app/ProductPage/FETCH_RELATED_LISTINGS_ERROR';

// ================ Reducer ================ //

const initialState = {
  showListingError: null,
  id: null,
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  relatedListingRefs: [],
  fetchRelatedListingsInProgress: false,
  fetchRelatedListingsError: null,
};

export default function productPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SHOW_LISTING_REQUEST:
      return { ...state, id: payload.params.id, showListingError: null };
    case SHOW_LISTING_SUCCESS:
      return state;
    case SHOW_LISTING_ERROR:
      return { ...state, showListingError: payload };

    case FETCH_LINE_ITEMS_REQUEST:
      return { ...state, fetchLineItemsInProgress: true, fetchLineItemsError: null };
    case FETCH_LINE_ITEMS_SUCCESS:
      return { ...state, fetchLineItemsInProgress: false, lineItems: payload };
    case FETCH_LINE_ITEMS_ERROR:
      return { ...state, fetchLineItemsInProgress: false, fetchLineItemsError: payload };

    case FETCH_RELATED_LISTINGS_REQUEST:
      return {
        ...state,
        fetchRelatedListingsInProgress: true,
        fetchRelatedListingsError: null,
      };
    case FETCH_RELATED_LISTINGS_SUCCESS:
      return {
        ...state,
        relatedListingRefs: payload.data.data.map(listing => ({
          id: listing.id,
          type: listing.type,
        })),
        fetchRelatedListingsInProgress: false,
        fetchRelatedListingsError: null,
      };
    case FETCH_RELATED_LISTINGS_ERROR:
      return {
        ...state,
        fetchRelatedListingsInProgress: false,
        fetchRelatedListingsError: payload,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const showListingRequest = requestAction(SHOW_LISTING_REQUEST);
export const showListingSuccess = successAction(SHOW_LISTING_SUCCESS);
export const showListingError = errorAction(SHOW_LISTING_ERROR);

export const fetchLineItemsRequest = () => ({ type: FETCH_LINE_ITEMS_REQUEST });
export const fetchLineItemsSuccess = lineItems => ({
  type: FETCH_LINE_ITEMS_SUCCESS,
  payload: lineItems,
});
export const fetchLineItemsError = error => ({
  type: FETCH_LINE_ITEMS_ERROR,
  error: true,
  payload: error,
});

export const fetchRelatedListingsRequest = () => ({ type: FETCH_RELATED_LISTINGS_REQUEST });
export const fetchRelatedListingsSuccess = response => ({
  type: FETCH_RELATED_LISTINGS_SUCCESS,
  payload: response,
});
export const fetchRelatedListingsError = error => ({
  type: FETCH_RELATED_LISTINGS_ERROR,
  error: true,
  payload: error,
});

// ================ Thunks ================ //

export const showListing = (listingId, config) => (dispatch, getState, sdk) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  dispatch(showListingRequest({ id: listingId }));

  // Fetch current user in lazy fashion
  const fetchCurrentUserOptions = {
    updateHasListings: false,
    updateNotifications: false,
  };
  dispatch(fetchCurrentUser(fetchCurrentUserOptions));

  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.image': [
      // Scaled variants for large images
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',
      // Listing image variants
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };

  return sdk.listings
    .show(params)
    .then(data => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      dispatch(addMarketplaceEntities(data, sanitizeConfig));
      dispatch(showListingSuccess(data));
      return data;
    })
    .catch(e => {
      dispatch(showListingError(storableError(e)));
      throw e;
    });
};

export const fetchTransactionLineItems = ({ orderData, listingId, isOwnListing }) => dispatch => {
  dispatch(fetchLineItemsRequest());
  transactionLineItems({ orderData, listingId, isOwnListing })
    .then(response => {
      const lineItems = response.data;
      dispatch(fetchLineItemsSuccess(lineItems));
    })
    .catch(e => {
      dispatch(fetchLineItemsError(storableError(e)));
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId.uuid,
        orderData,
        statusText: e.statusText,
      });
    });
};

export const fetchRelatedListings = ({ brandSlug, category, currentListingId } = {}) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(fetchRelatedListingsRequest());

  if (!sdk?.listings?.query) {
    const emptyResponse = { data: { data: [] } };
    dispatch(fetchRelatedListingsSuccess(emptyResponse));
    return Promise.resolve(emptyResponse);
  }

  const baseParams = {
    pub_listingType: 'sell-used-products',
    sort: '-createdAt',
    perPage: 12,
    include: ['images'],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.listing-card',
      'variants.listing-card-2x',
    ],
    ...createImageVariantConfig('listing-card', 400, 1.34),
    ...createImageVariantConfig('listing-card-2x', 800, 1.34),
    'limit.images': 1,
  };

  const randomFallbackParams = {
    ...baseParams,
    perPage: 30,
  };

  const queryAttempts = [
    brandSlug ? { ...baseParams, pub_brandSlug: brandSlug } : null,
    category ? { ...baseParams, pub_categoryLevel1: category } : null,
    randomFallbackParams,
  ].filter(Boolean);

  const filterCurrentListing = response => {
    const listings = response?.data?.data || [];

    if (!currentListingId) {
      return response;
    }

    const filteredListings = listings.filter(listing => listing?.id?.uuid !== currentListingId);

    if (filteredListings.length === listings.length) {
      return response;
    }

    return {
      ...response,
      data: {
        ...response.data,
        data: filteredListings,
      },
    };
  };

  const runQueryAt = index => {
    const params = queryAttempts[index];
    return sdk.listings.query(params).then(response => {
      const filteredResponse = filterCurrentListing(response);

      dispatch(addMarketplaceEntities(filteredResponse));

      const hasResults = (filteredResponse?.data?.data || []).length > 0;
      const isLastAttempt = index === queryAttempts.length - 1;

      if (hasResults || isLastAttempt) {
        const shouldRandomizeFallback =
          isLastAttempt && !brandSlug && !category ? false : isLastAttempt;
        const randomizedResponse = shouldRandomizeFallback
          ? {
              ...filteredResponse,
              data: {
                ...filteredResponse.data,
                data: shuffleArray(filteredResponse?.data?.data || []),
              },
            }
          : filteredResponse;

        dispatch(fetchRelatedListingsSuccess(randomizedResponse));
        return randomizedResponse;
      }

      return runQueryAt(index + 1);
    });
  };

  return runQueryAt(0).catch(error => {
    dispatch(fetchRelatedListingsError(storableError(error)));
    throw error;
  });
};

export const loadData = (params, search, config) => dispatch => {
  const listingId = new types.UUID(params.id);
  return dispatch(showListing(listingId, config));
};

export const getRelatedListings = state => {
  const refs = state.ProductPage?.relatedListingRefs || [];
  return getMarketplaceEntities(state, refs);
};

// Import SDK types
import { types } from '../../util/sdkLoader';
